import React, { useState, useEffect, useRef } from 'react';
import { 
  FaCar, FaPlane, FaCity, FaTrain, 
  FaArrowRight, FaStar, FaShieldAlt, FaClock, 
  FaUsers, FaCheckCircle, FaMountain,
  FaMapMarkerAlt, FaCalendarAlt, FaUserFriends,
  FaHeart, FaShareAlt, FaChevronRight,
  FaTag, FaLeaf, FaCrown, FaGem,
  FaMagic, FaPalette
} from 'react-icons/fa';
import '../style.css';

const JourneyStartHere = () => {
  const [activeCard, setActiveCard] = useState(1);
  const [favorites, setFavorites] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoadStatus, setImageLoadStatus] = useState({});
  const sectionRef = useRef(null);

  const services = [
    {
      id: 1,
      title: "Premium Airport Transfers",
      description: "Experience luxury from the moment you land. Our professional chauffeurs provide seamless airport pickups with real-time flight tracking and premium amenities.",
      shortDesc: "Luxury airport transportation",
      icon: <FaPlane />,
      image: "https://www.roldrive.com/images/banner/airport-transfer.jpg",
      landscapeImage: "https://images.unsplash.com/photo-1576675466969-38eeae4b41f6?ixlib=rb-1.2.1&auto=format&fit=crop&w=1600&q=80",
      color: "#8B5CF6",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
      features: ["Flight Tracking", "Meet & Greet", "Free Waiting", "VIP Service"],
      stats: { trips: "5K+", rating: "4.9" },
      price: "₹1,499",
      duration: "2-4 hours",
      capacity: "4 persons",
      included: ["Free WiFi", "Premium Refreshments", "Daily Newspapers", "Phone Chargers"],
      highlights: ["Real-time flight updates", "Red carpet service", "Professional chauffeurs"]
    },
    {
      id: 2,
      title: "Cultural Heritage Tours",
      description: "Discover Thailand's rich cultural heritage with expert local guides. Visit ancient temples, vibrant markets, and hidden gems with personalized itineraries.",
      shortDesc: "Immerse in Thai culture",
      icon: <FaCity />,
      image: "https://vl-prod-static.b-cdn.net/system/images/000/846/318/9e65853ee91e47adcbeacaa2831061c5/original/Heritage_tour.png",
      landscapeImage: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?ixlib=rb-1.2.1&auto=format&fit=crop&w=1600&q=80",
      color: "#10B981",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
      features: ["Local Guides", "Cultural Insights", "Flexible Routes", "Photography"],
      stats: { tours: "200+", rating: "4.8" },
      price: "₹2,999",
      duration: "6-8 hours",
      capacity: "6 persons",
      included: ["Expert Guide", "Entry Tickets", "Traditional Lunch", "Souvenir"],
      highlights: ["Expert local guides", "Hidden cultural gems", "Flexible personalized itinerary"]
    },
    {
      id: 3,
      title: "Mountain Escapes",
      description: "Escape to Thailand's breathtaking hill stations. Travel in comfort through scenic routes with multiple stops and professional driver service.",
      shortDesc: "Scenic mountain journeys",
      icon: <FaMountain />,
      image: "https://tse1.mm.bing.net/th/id/OIP.BZezskRLIJVwZo4zhJziWwHaEc?w=1000&h=600&rs=1&pid=ImgDetMain&o=7&rm=3",
      landscapeImage: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-1.2.1&auto=format&fit=crop&w=1600&q=80",
      color: "#F59E0B",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
      features: ["Scenic Routes", "Multiple Stops", "Driver Stay", "All Inclusive"],
      stats: { cities: "50+", rating: "4.9" },
      price: "₹3,999",
      duration: "Full Day",
      capacity: "7 persons",
      included: ["Toll Charges", "Driver Accommodation", "Breakfast", "Parking"],
      highlights: ["Breathtaking mountain views", "Flexible multiple stops", "Comfortable long journeys"]
    },
    {
      id: 4,
      title: "Luxury SUV Experience",
      description: "Travel in ultimate luxury with our premium SUV fleet. Enjoy VIP amenities, professional chauffeurs, and unmatched comfort for your journey.",
      shortDesc: "Ultimate luxury travel",
      icon: <FaCar />,
      image: "https://carscabs.com/wp-content/uploads/2023/06/Grand-Highlander.jpg",
      landscapeImage: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1600&q=80",
      color: "#EC4899",
      gradient: "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)",
      features: ["WiFi Onboard", "Premium Refreshments", "Extra Luggage", "VIP Chauffeur"],
      stats: { vehicles: "25+", rating: "5.0" },
      price: "₹4,499",
      duration: "Custom",
      capacity: "8 persons",
      included: ["Premium SUV", "Professional Chauffeur", "Refreshments", "WiFi"],
      highlights: ["Luxury premium vehicles", "Professional chauffeur service", "VIP amenities included"]
    }
  ];

  const toggleFavorite = (id, e) => {
    if (e) e.stopPropagation();
    setFavorites(prev => 
      prev.includes(id) 
        ? prev.filter(favId => favId !== id)
        : [...prev, id]
    );
  };

  const handleBookNow = (service, e) => {
    if (e) e.stopPropagation();
    alert(`Booking ${service.title} for ${service.price}`);
  };

  const handleImageLoad = (id) => {
    setImageLoadStatus(prev => ({
      ...prev,
      [id]: true
    }));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const activeService = services.find(service => service.id === activeCard);

  return (
    <section className="travel-section-enhanced" ref={sectionRef}>
      {/* Animated Background Particles */}
      <div className="particles-container">
        {[...Array(15)].map((_, i) => (
          <div key={i} className="particle" style={{
            animationDelay: `${i * 0.2}s`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`
          }}></div>
        ))}
      </div>

      <div className="container-enhanced">
        {/* Elegant Header */}
        <div className={`header-enhanced ${isVisible ? 'visible' : ''}`}>
          <div className="header-badge-enhanced">
            <FaMagic className="sparkle-icon" />
            <span>Premium Travel Experiences</span>
            <FaMagic className="sparkle-icon" />
          </div>
          
          <h1 className="title-enhanced">
            <span className="title-word">Discover</span>
            <span className="title-word highlight-gradient">Extraordinary</span>
            <span className="title-word">Journeys</span>
          </h1>
          
          <p className="subtitle-enhanced">
            Experience Thailand like never before with our premium travel services. 
            From luxury airport transfers to cultural adventures, we create memories that last.
          </p>
        </div>

        {/* Main Content Layout */}
        <div className="content-layout-enhanced">
          {/* Side Navigation */}
          <div className={`side-navigation ${isVisible ? 'slide-in-left' : ''}`}>
            <div className="nav-stack">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  className={`nav-item ${activeCard === service.id ? 'active' : ''}`}
                  onClick={() => setActiveCard(service.id)}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="nav-item-content">
                    <div 
                      className="nav-icon-enhanced"
                      style={{ 
                        background: service.gradient,
                        boxShadow: `0 10px 30px ${service.color}40`
                      }}
                    >
                      {service.icon}
                    </div>
                    
                    <div className="nav-info">
                      <h3>{service.title}</h3>
                      <p className="nav-desc">{service.shortDesc}</p>
                      
                      <div className="nav-tags">
                        <span className="tag">
                          <FaClock /> {service.duration}
                        </span>
                        <span className="tag">
                          <FaUsers /> {service.capacity}
                        </span>
                      </div>
                    </div>
                    
                    <div className="nav-arrow">
                      <FaArrowRight />
                    </div>
                  </div>
                  
                  <div className="nav-hover-glow" style={{ background: service.gradient }}></div>
                </div>
              ))}
            </div>

            {/* Quick Stats */}
            <div className="quick-stats">
              <div className="stat-enhanced">
                <div className="stat-number">10K+</div>
                <div className="stat-label">Happy Travelers</div>
              </div>
              <div className="stat-enhanced">
                <div className="stat-number">500+</div>
                <div className="stat-label">Destinations</div>
              </div>
            </div>
          </div>

          {/* Main Feature Card */}
          <div className={`main-feature-card ${isVisible ? 'slide-in-right' : ''}`}>
            <div className="feature-card-inner">
              {/* Card Header with Glow */}
              <div className="feature-header">
                <div className="feature-badge" style={{ background: activeService?.gradient }}>
                  <FaCrown />
                  <span>Premium Selection</span>
                </div>
                
                <div className="header-actions">
                  <button 
                    className={`fav-btn-enhanced ${favorites.includes(activeCard) ? 'active' : ''}`}
                    onClick={() => toggleFavorite(activeCard)}
                  >
                    <FaHeart />
                  </button>
                  <button className="share-btn-enhanced">
                    <FaShareAlt />
                  </button>
                </div>
              </div>

              {/* Main Image - Full Display */}
              <div className="feature-image-section">
                <div className="image-wrapper">
                  <img 
                    src={activeService?.landscapeImage}
                    alt={activeService?.title}
                    className="feature-image"
                    onLoad={() => handleImageLoad(activeCard)}
                    style={{ opacity: imageLoadStatus[activeCard] ? 1 : 0 }}
                  />
                  
                  {/* Loading Placeholder */}
                  {!imageLoadStatus[activeCard] && (
                    <div className="image-loading">
                      <div className="loading-spinner"></div>
                    </div>
                  )}
                  
                  {/* Image Overlay */}
                  <div className="feature-image-overlay">
                    <div className="overlay-content">
                      <h2 className="image-title">{activeService?.title}</h2>
                      <div className="image-rating">
                        <div className="stars">
                          {[...Array(5)].map((_, i) => (
                            <FaStar key={i} className={i < Math.floor(parseFloat(activeService?.stats.rating || 0)) ? 'filled' : ''} />
                          ))}
                        </div>
                        <span className="rating-text">{activeService?.stats.rating} • {activeService?.stats.trips} trips</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="feature-content">
                <p className="feature-description">
                  {activeService?.description}
                </p>

                {/* Service Highlights */}
                <div className="highlights-grid">
                  {activeService?.highlights.map((highlight, index) => (
                    <div key={index} className="highlight-item-enhanced">
                      <div className="highlight-icon" style={{ color: activeService?.color }}>
                        <FaGem />
                      </div>
                      <span>{highlight}</span>
                    </div>
                  ))}
                </div>

                {/* Service Details */}
                <div className="service-details-enhanced">
                  <div className="detail-row">
                    <div className="detail-enhanced">
                      <div className="detail-icon-wrapper" style={{ background: `${activeService?.color}20` }}>
                        <FaCalendarAlt style={{ color: activeService?.color }} />
                      </div>
                      <div className="detail-info">
                        <div className="detail-label">Duration</div>
                        <div className="detail-value">{activeService?.duration}</div>
                      </div>
                    </div>
                    
                    <div className="detail-enhanced">
                      <div className="detail-icon-wrapper" style={{ background: `${activeService?.color}20` }}>
                        <FaUserFriends style={{ color: activeService?.color }} />
                      </div>
                      <div className="detail-info">
                        <div className="detail-label">Capacity</div>
                        <div className="detail-value">{activeService?.capacity}</div>
                      </div>
                    </div>
                    
                    <div className="detail-enhanced">
                      <div className="detail-icon-wrapper" style={{ background: `${activeService?.color}20` }}>
                        <FaMapMarkerAlt style={{ color: activeService?.color }} />
                      </div>
                      <div className="detail-info">
                        <div className="detail-label">Popularity</div>
                        <div className="detail-value">{activeService?.stats.trips} trips</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Section */}
                <div className="cta-enhanced">
                  <div className="price-section-enhanced">
                    <div className="price-label">Starting from</div>
                    <div className="price-main-enhanced">
                      <span className="currency">₹</span>
                      <span className="price-amount-enhanced">{activeService?.price}</span>
                      <span className="price-unit">/day</span>
                    </div>
                    <div className="price-note">All inclusive • No hidden fees</div>
                  </div>
                  
                  <button 
                    className="book-now-enhanced"
                    style={{ 
                      background: activeService?.gradient,
                      boxShadow: `0 10px 40px ${activeService?.color}40`
                    }}
                    onClick={() => handleBookNow(activeService)}
                  >
                    <span>Book This Experience</span>
                    <div className="btn-sparkles">
                      <FaMagic />
                      <FaMagic />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Services Grid - FIXED IMAGE DISPLAY */}
        <div className={`bottom-services-enhanced ${isVisible ? 'fade-in-up' : ''}`}>
          <div className="section-title-bottom">
            <div className="title-decoration">
              <FaPalette className="decoration-icon" />
              <h2>Explore All Premium Services</h2>
              <FaPalette className="decoration-icon" />
            </div>
            <p className="section-subtitle">Choose from our curated collection of exceptional travel experiences</p>
          </div>

          <div className="services-grid-enhanced">
            {services.map((service) => (
              <div
                key={service.id}
                className={`service-card-enhanced ${activeCard === service.id ? 'selected' : ''}`}
                onClick={() => setActiveCard(service.id)}
              >
                {/* Card Image - FIXED: Full Display */}
                <div className="card-image-full">
                  <div className="image-container-full">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="service-image-full"
                      onLoad={() => handleImageLoad(service.id)}
                      style={{ opacity: imageLoadStatus[service.id] ? 1 : 0 }}
                    />
                    
                    {/* Image Overlay for Better Readability */}
                    <div className="card-image-overlay" style={{ background: `linear-gradient(to bottom, transparent 40%, ${service.color}40 100%)` }}></div>
                    
                    {/* Rating Badge */}
                    <div className="card-rating">
                      <FaStar className="rating-star" />
                      <span>{service.stats.rating}</span>
                    </div>
                    
                    {/* Favorite Button */}
                    <button 
                      className={`card-fav-btn-enhanced ${favorites.includes(service.id) ? 'active' : ''}`}
                      onClick={(e) => toggleFavorite(service.id, e)}
                    >
                      <FaHeart />
                    </button>
                  </div>
                </div>

                {/* Card Content */}
                <div className="card-content-enhanced">
                  <div className="card-header-enhanced">
                    <div 
                      className="service-icon-card"
                      style={{ background: service.gradient }}
                    >
                      {service.icon}
                    </div>
                    <h3>{service.title}</h3>
                  </div>
                  
                  <p className="card-description-enhanced">{service.shortDesc}</p>
                  
                  <div className="card-features-enhanced">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="feature-tag-enhanced">
                        {feature}
                      </span>
                    ))}
                  </div>
                  
                  <div className="card-meta-enhanced">
                    <div className="meta-item-enhanced">
                      <FaClock />
                      <span>{service.duration}</span>
                    </div>
                    <div className="meta-divider">•</div>
                    <div className="meta-item-enhanced">
                      <FaUsers />
                      <span>{service.capacity}</span>
                    </div>
                  </div>
                  
                  <div className="card-footer-enhanced">
                    <div className="price-card-enhanced">
                      <span className="price-label-card">From</span>
                      <span className="price-amount-card">{service.price}</span>
                      <span className="price-unit-card">/day</span>
                    </div>
                    <button 
                      className="book-btn-enhanced"
                      style={{ background: service.gradient }}
                      onClick={(e) => handleBookNow(service, e)}
                    >
                      Quick Book
                    </button>
                  </div>
                </div>

                {/* Selection Indicator */}
                {activeCard === service.id && (
                  <div className="selection-indicator" style={{ background: service.gradient }}>
                    <FaMagic className="indicator-sparkle" />
                    <span>Currently Viewing</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* View All Button */}
          <div className="view-all-enhanced">
            <button className="view-all-btn-enhanced">
              <span>Discover More Experiences</span>
              <FaChevronRight className="view-all-arrow" />
              <div className="btn-shine-enhanced"></div>
            </button>
          </div>
        </div>

       
      </div>
    </section>
  );
};

export default JourneyStartHere;